import React, { useState } from "react";
import { toast } from "react-toastify";

import Modal from "../../../../components/Reusable/Modal";
import RichTextEditor from "../../../../components/Reusable/RichTextEditor";
import { useAppDispatch } from "../../../../hooks/hooks";
import {
  createGamecardProvider,
  fetchGamecardProviders,
} from "../../../../features/auth/store/gamecardProviderSlice";
import { Props } from "../types";

export const CreateGamecardProviderModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<{
    name: string;
    slug: string;
    description: string;
    status: "on" | "off";
    recipient_label: string;
    verify_recipient: boolean;
    additional_input: boolean;
    additional_input_placeholder: string;
    additional_input_type: string;
    additional_input_label: string;
    additional_input_form_name: string;
  }>({
    name: "",

    slug: "",
    description: "",
    status: "on",
    recipient_label: "",
    verify_recipient: false,
    additional_input: false,
    additional_input_placeholder: "",
    additional_input_type: "",
    additional_input_label: "",
    additional_input_form_name: "",
  });
  const [image, setImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    if (e.target instanceof HTMLInputElement && e.target.type === "checkbox") {
      setForm({ ...form, [e.target.name]: e.target.checked });
      return;
    }
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const removeImage = () => setImage(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("slug", form.slug);
      formData.append("description", form.description);
      formData.append("status", form.status);
      formData.append("recipient_label", form.recipient_label);
      formData.append("verify_recipient", form.verify_recipient ? "1" : "0");
      formData.append("additional_input", form.additional_input ? "1" : "0");
      formData.append(
        "additional_input_placeholder",
        form.additional_input_placeholder,
      );
      formData.append("additional_input_type", form.additional_input_type);
      formData.append("additional_input_label", form.additional_input_label);
      formData.append(
        "additional_input_form_name",
        form.additional_input_form_name,
      );
      if (image) {
        formData.append("image", image);
      }
      await dispatch(createGamecardProvider(formData)).unwrap();
      toast.success("Provider created!");
      onClose();
      dispatch(fetchGamecardProviders({}));
    } catch (err: any) {
      toast.error(err?.message || "Failed to create provider");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="w-full max-w-md md:max-w-lg lg:max-w-xl mx-auto"
    >
      <div className="max-h-[90vh] overflow-y-auto pr-1">
        <h2 className="text-lg md:text-xl font-bold mb-6">
          Create Game Card Provider
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block mb-2">Name</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="w-full p-2 border rounded  dark:bg-white/10 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Slug</label>
            <input
              type="text"
              name="slug"
              value={form.slug}
              onChange={handleChange}
              required
              className="w-full p-2 border rounded  dark:bg-white/10 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Status</label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="w-full p-2 border rounded  dark:bg-white/10 dark:text-white"
            >
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Recipient Label</label>
            <input
              type="text"
              name="recipient_label"
              value={form.recipient_label}
              onChange={handleChange}
              className="w-full p-2 border rounded  dark:bg-white/10 dark:text-white"
            />
          </div>
          <div className="mb-4 flex items-center gap-2">
            <input
              id="verify_recipient"
              type="checkbox"
              name="verify_recipient"
              checked={form.verify_recipient}
              onChange={handleChange}
            />
            <label htmlFor="verify_recipient">Verify Recipient</label>
          </div>
          <div className="mb-4 flex items-center gap-2">
            <input
              id="additional_input"
              type="checkbox"
              name="additional_input"
              checked={form.additional_input}
              onChange={handleChange}
            />
            <label htmlFor="additional_input">Additional Input</label>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Additional Input Placeholder</label>
            <input
              type="text"
              name="additional_input_placeholder"
              value={form.additional_input_placeholder}
              onChange={handleChange}
              className="w-full p-2 border rounded  dark:bg-white/10 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Additional Input Type</label>
            <input
              type="text"
              name="additional_input_type"
              value={form.additional_input_type}
              onChange={handleChange}
              className="w-full p-2 border rounded  dark:bg-white/10 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Additional Input Label</label>
            <input
              type="text"
              name="additional_input_label"
              value={form.additional_input_label}
              onChange={handleChange}
              className="w-full p-2 border rounded  dark:bg-white/10 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Additional Input Form Name</label>
            <input
              type="text"
              name="additional_input_form_name"
              value={form.additional_input_form_name}
              onChange={handleChange}
              className="w-full p-2 border rounded  dark:bg-white/10 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Description</label>
            <RichTextEditor
              value={form.description}
              onChange={(value) => setForm({ ...form, description: value })}
              placeholder="Write provider description..."
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Image</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center  dark:bg-white/10 dark:text-white">
              <input
                type="file"
                id="providerImageUpload"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="providerImageUpload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mb-2"
                >
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17 8L12 3L7 8"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm text-gray-600">
                  Upload from computer
                </span>
              </label>
            </div>
            {image && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="relative rounded-md overflow-hidden h-20">
                  <img
                    src={URL.createObjectURL(image)}
                    alt="provider"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-1 right-1 bg-white rounded-full p-1"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 6L18 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full bg-gradient-to-r from-[#027EC2] to-[#039EF2] h-10 text-white rounded ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "Creating..." : "Create Provider"}
          </button>
        </form>
      </div>
    </Modal>
  );
};
